/**
 * bannerDismissal — pure helpers for the "returns every morning" banner
 * dismissal policy (owner directive 2026-08-14: an X-dismissal expires at
 * 5:00 AM local the next morning, so the strip re-appears each day).
 *
 * Pure date math + guarded localStorage wrappers — no React, unit-testable
 * (src/components/common/__tests__/bannerDismissal.test.ts).
 */

/** The next 5:00:00 AM *local* strictly after `date` (a 5:00:00.000 input → 5 AM the NEXT day). */
export function nextFiveAmAfter(date: Date): Date {
  const next = new Date(date.getTime());
  next.setHours(5, 0, 0, 0);
  if (next.getTime() <= date.getTime()) {
    next.setDate(next.getDate() + 1);
  }
  return next;
}

/**
 * True only while `now` is before the 5 AM following the dismissal — i.e. the
 * dismissal "holds" until the next morning. Absent/malformed timestamps count
 * as NOT dismissed (fail toward showing the banner).
 */
export function isDismissalActive(dismissedAtIso: string | null, now: Date = new Date()): boolean {
  if (!dismissedAtIso) return false;
  const dismissedAt = new Date(dismissedAtIso);
  if (Number.isNaN(dismissedAt.getTime())) return false;
  return now.getTime() < nextFiveAmAfter(dismissedAt).getTime();
}

/** Read the stored dismissal timestamp (ISO string) — null on absence or storage failure. */
export function readDailyDismissal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Stamp a dismissal at `now` (ISO). Storage failures are swallowed — worst case the banner re-shows. */
export function writeDailyDismissal(key: string, now: Date = new Date()): void {
  try {
    localStorage.setItem(key, now.toISOString());
  } catch {
    /* ignore — fail toward re-showing */
  }
}
